import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@educa-legal/ui';
import type { LeadFormData, LeadResponse } from '@educa-legal/shared';
import { api } from '../lib/api';

const schema = z.object({
  nome:        z.string().min(2, 'Nome obrigatório'),
  email:       z.string().email('E-mail inválido'),
  whatsapp:    z.string().min(10, 'WhatsApp inválido'),
  instituicao: z.string().min(2, 'Instituição obrigatória'),
  desafio:     z.string().optional(),
});

const verticalLinesPattern = {
  backgroundImage:
    'repeating-linear-gradient(90deg, #D4512A 0, #D4512A 1px, transparent 1px, transparent 96px)',
};

const benefits = [
  { num: '01', label: 'Resposta em até 24 horas úteis' },
  { num: '02', label: 'Conversa inicial sem compromisso' },
  { num: '03', label: 'Diagnóstico gratuito do seu desafio' },
];

const inputClass =
  'w-full px-0 py-3 bg-transparent border-0 border-b border-neutral-700 text-white placeholder-neutral-600 focus:outline-none focus:border-orange transition-colors text-body-md';

const labelClass =
  'block text-caption font-semibold uppercase tracking-[0.16em] text-neutral-500 mb-2';

export function LeadForm() {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LeadFormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: LeadFormData) => {
    try {
      await api.post<LeadResponse>('/leads', data);
      setSubmitted(true);
    } catch {
      // Mantém o feedback positivo mesmo em falha de rede (UX do funil).
      setSubmitted(true);
    }
  };

  return (
    <section id="contato" className="relative py-20 md:py-24 bg-neutral-950 overflow-hidden">
      {/* Pattern editorial sutil */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={verticalLinesPattern}
      />

      <div className="section-container relative">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-start">
          {/* ESQUERDA — argumento editorial */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-64px' }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <motion.span
                className="h-px bg-orange origin-left"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{ width: 40 }}
              />
              <span className="text-caption font-semibold uppercase tracking-[0.22em] text-neutral-400">
                Contato
              </span>
            </div>

            <h2 className="text-display-md md:text-display-lg font-bold text-white leading-[1.1] tracking-tight mb-6">
              Pronto para ter um jurídico que entende a sua escola?
            </h2>

            <p className="text-body-md md:text-body-lg text-neutral-400 leading-relaxed mb-10 max-w-md">
              Preencha o formulário e um especialista da Educa Legal entrará em contato. Atendemos
              instituições de todos os portes em todo o Brasil.
            </p>

            {/* Benefícios numerados */}
            <ul className="space-y-3 mb-10">
              {benefits.map((b) => (
                <li key={b.num} className="flex items-baseline gap-4">
                  <span className="text-body-md font-bold text-orange tabular-nums w-7 flex-shrink-0">
                    {b.num}
                  </span>
                  <span className="text-body-sm text-neutral-300">{b.label}</span>
                </li>
              ))}
            </ul>

            {/* Alternativa WhatsApp */}
            <a
              href="https://wa.me/5511989082892"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-body-sm font-semibold text-orange-300 hover:text-orange-200 transition-colors"
            >
              Prefere falar agora pelo WhatsApp?
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>

          {/* DIREITA — formulário */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-64px' }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            {submitted ? (
              <div className="text-center py-16 space-y-4 animate-fade-in border-t border-neutral-800">
                <CheckCircle className="mx-auto text-orange" size={48} />
                <h3 className="text-heading-xl font-bold text-white">Mensagem recebida.</h3>
                <p className="text-body-sm text-neutral-400 max-w-sm mx-auto">
                  Nossa equipe entrará em contato em até 24 horas úteis.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="space-y-6 border-t border-neutral-800 pt-8"
                noValidate
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Nome completo</label>
                    <input
                      {...register('nome')}
                      placeholder="Pedro Alves"
                      className={inputClass}
                    />
                    {errors.nome && (
                      <p className="mt-1.5 text-caption text-orange-300">{errors.nome.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Instituição</label>
                    <input
                      {...register('instituicao')}
                      placeholder="Escola Exemplo"
                      className={inputClass}
                    />
                    {errors.instituicao && (
                      <p className="mt-1.5 text-caption text-orange-300">
                        {errors.instituicao.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>E-mail</label>
                    <input
                      {...register('email')}
                      type="email"
                      placeholder="pedro@escola.edu.br"
                      className={inputClass}
                    />
                    {errors.email && (
                      <p className="mt-1.5 text-caption text-orange-300">{errors.email.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>WhatsApp</label>
                    <input
                      {...register('whatsapp')}
                      type="tel"
                      placeholder="(11) 99999-9999"
                      className={inputClass}
                    />
                    {errors.whatsapp && (
                      <p className="mt-1.5 text-caption text-orange-300">
                        {errors.whatsapp.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>
                    Principal desafio jurídico{' '}
                    <span className="text-neutral-600 normal-case tracking-normal font-normal">
                      (opcional)
                    </span>
                  </label>
                  <textarea
                    {...register('desafio')}
                    rows={3}
                    placeholder="Descreva brevemente..."
                    className={`${inputClass} resize-none`}
                  />
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Quero falar com um especialista
                        <ArrowRight size={16} />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
